import {
  float,
  floor,
  hash,
  instanceIndex,
  length,
  mix,
  pow,
  sin,
  smoothstep,
  uniform,
  uv,
  vec3,
  vec4,
} from 'three/tsl';
import * as THREE from 'three/webgpu';
import { additiveOutputs } from '../render/sceneOutputs';

type N = any;
type RGB = [number, number, number];
const _up = new THREE.Vector3();

/** Drifting motes of light (spores, embers, fireflies) around the camera. */
export interface MoteDef {
  count: number;
  /** Half-size of the box around the camera that the motes fill (m). */
  radius: number;
  /** Sprite size range (m). */
  size: [number, number];
  colors: [RGB, RGB];
  /** Radiance at full glow. */
  brightness: number;
  /** Drift speed (m/s). */
  drift: number;
  /** Upward drift (m/s), e.g. rising embers. */
  rise?: number;
  /** Optional brightness multiplier (e.g. the light rig's glow phase at the camera). */
  glow?: () => N;
}

/**
 * Motes live at fixed places in the hollow's frame on a repeating 3D lattice
 * (a box of 2·radius wrapped around the camera), so walking through them feels
 * like moving through a field of them. Each drifts, bobs and twinkles on its own.
 * Drawn as additive, depth-tested sprites.
 */
export class Motes {
  readonly mesh: THREE.Sprite;
  private readonly time = uniform(0);
  private readonly camLocal = uniform(new THREE.Vector3());
  /** Accumulated rise, integrated on the CPU along the local up (kept small by wrapping). */
  private readonly riseOffset = uniform(new THREE.Vector3());
  private readonly rise: number;
  private readonly boxSize: number;

  constructor(def: MoteDef) {
    const mat = new THREE.SpriteNodeMaterial();
    mat.transparent = true;
    mat.depthWrite = false;
    mat.blending = THREE.AdditiveBlending;
    const R = def.radius;
    this.rise = def.rise ?? 0;
    this.boxSize = 2 * R;
    const i = float(instanceIndex);
    // TSL hash takes an integer seed: 17 independent streams per mote.
    const h = (k: number) => hash(i.mul(17).add(k));
    const t = this.time;
    // World-fixed base position plus drift and bob, then wrapped around the camera.
    const base = vec3(h(1), h(2), h(3)).mul(2 * R);
    const dir = vec3(h(4), h(5), h(6)).sub(0.5).mul(2);
    const bob = vec3(
      sin(t.mul(h(7).mul(0.6).add(0.3)).add(h(8).mul(6.28))),
      sin(t.mul(h(9).mul(0.5).add(0.25)).add(h(10).mul(6.28))),
      sin(t.mul(h(11).mul(0.7).add(0.2)).add(h(12).mul(6.28))),
    ).mul(1.2);
    const world = base
      .add(dir.mul(t.mul(def.drift)))
      .add(bob)
      .add(this.riseOffset);
    const rel = world.sub(this.camLocal);
    const wrapped = rel.sub(floor(rel.add(R).div(2 * R)).mul(2 * R));
    mat.positionNode = wrapped;
    mat.scaleNode = mix(float(def.size[0]), float(def.size[1]), h(13));

    const d = length(wrapped);
    const edge = float(1).sub(smoothstep(R * 0.55, R * 0.95, d));
    const nearFade = smoothstep(0.6, 2.5, d);
    const twinkle = pow(
      sin(t.mul(h(14).mul(2.5).add(0.6)).add(h(15).mul(6.28)))
        .mul(0.5)
        .add(0.5),
      2,
    );
    const r = length(uv().sub(0.5)).mul(2);
    const disc = pow(float(1).sub(smoothstep(0, 1, r)), 2.2);
    const col = mix(vec3(...def.colors[0]), vec3(...def.colors[1]), h(16));
    let bright: N = float(def.brightness).mul(twinkle.mul(0.8).add(0.2)).mul(edge).mul(nearFade);
    if (def.glow) bright = bright.mul(def.glow());
    mat.colorNode = vec4(col.mul(disc).mul(bright), 1);
    // Light only: the normal and velocity buffers keep the surface behind.
    mat.mrtNode = additiveOutputs();

    this.mesh = new THREE.Sprite(mat);
    this.mesh.count = def.count;
    this.mesh.frustumCulled = false;
    this.mesh.name = 'motes';
  }

  /** camLocal: camera position in the hollow's frame. */
  update(camLocal: THREE.Vector3, dt: number): void {
    this.time.value += dt;
    this.camLocal.value.copy(camLocal);
    if (this.rise > 0) {
      const o = this.riseOffset.value;
      o.addScaledVector(_up.copy(camLocal).normalize(), -this.rise * dt);
      const s = this.boxSize;
      o.set(o.x - s * Math.round(o.x / s), o.y - s * Math.round(o.y / s), o.z - s * Math.round(o.z / s));
    }
  }
}
